import React, { ReactNode, useMemo } from 'react';

import {
  AttendanceStatusType,
  EnrollResponseDataType,
  getApplicationTypeDetailLabel,
  getGeneralEnrollCount,
  IEnroll,
  isAdminApplicationType,
  SelectionStatusType,
} from 'types';
import { Link } from 'react-router-dom';
import { CheckBox } from 'components/form';
import useEroll from './useEnroll';
import dayjs from 'dayjs';
import useCheckTable from 'hooks/useCheckTable';
import { EnrollQuery, PushQuery } from 'queries';
import useModal from 'hooks/useModal';
import useDetail from '../useDetail';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { USER_MENU_URL } from 'data/menu';
import { formatPhoneNumber } from 'utils/format';
import { useAppSelector } from 'stores';

const COLS = [
  '참여유형',
  '이름',
  '성별',
  '연락처',
  '신청일',
  '최근참여일',
  '출결상태',
  '피드백작성여부',
  '추가정보',
  '메모',
];

interface Props {
  hanldeOpenUserMemo: (userId: number) => void;
  handleOpenAdditional: (data: EnrollResponseDataType) => void;
  handleOpenFeedback: (data: EnrollResponseDataType) => void;
}

function useVolunteerList({
  hanldeOpenUserMemo,
  handleOpenAdditional,
  handleOpenFeedback,
}: Props) {
  const { event } = useDetail();
  // 회원 상세·메모는 PlatformSuper 전용
  const canViewUser = useAppSelector(
    (s) => s.auth.user?.permissions.includes('user:detail') ?? false,
  );
  const { mutate: postFeedbackNotification } =
    PushQuery.usePostFeedbackNotification();

  const isLottery = event?.recruitmentType === 'LOTTERY';

  const [nameKeyword, setNameKeyword] = React.useState('');
  const [attendanceFilter, setAttendanceFilter] = React.useState<string>();
  const [selectionFilter, setSelectionFilter] = React.useState<string>();

  const { data, pageState } = useEroll(
    nameKeyword,
    attendanceFilter,
    selectionFilter,
  );

  const resetPage = React.useCallback(
    () => pageState.handlePage((prev) => ({ ...prev, page: 1 })),
    [pageState],
  );

  const handleSearchName = React.useCallback(
    (keyword: string) => {
      setNameKeyword(keyword);
      resetPage();
    },
    [resetPage],
  );

  const handleChangeAttendanceFilter = React.useCallback(
    (param?: string) => {
      setAttendanceFilter(param);
      resetPage();
    },
    [resetPage],
  );

  const handleChangeSelectionFilter = React.useCallback(
    (param?: string) => {
      setSelectionFilter(param);
      resetPage();
    },
    [resetPage],
  );

  const { data: summary } = EnrollQuery.useGetSummaryByEvent(
    event?.id ? String(event.id) : '',
  );
  const hasSelected = Number(summary?.selectedGeneralCount ?? 0) > 0;
  // 어드민 수기 추가분은 제거 대상
  const generalEnrollCount = getGeneralEnrollCount(summary);

  const { mutate, isPending: isChangingStatus } = EnrollQuery.usePatchStatus();
  const { mutate: removeEnrolls, isPending: isRemoving } =
    EnrollQuery.useRemove();
  const { mutate: confirmSelection, isPending: isConfirming } =
    EnrollQuery.useConfirmSelection();
  const { mutate: selectAdditional, isPending: isSelectingAdditional } =
    EnrollQuery.useSelectAdditional();

  const { handleConfirm } = useModal();

  const { isAll, handleAllCheck, checkedData, handleCheck } = useCheckTable({
    data: data?.data || [],
  });

  const getCheckedEnrollIds = () =>
    Object.keys(checkedData)
      .filter((key) => checkedData[Number(key)])
      .map((key) => Number(key));

  const checkedEnrolls: IEnroll[] = (data?.data ?? []).filter(
    (enroll: IEnroll) => checkedData[enroll.id],
  );

  const cols = useMemo(
    () => [
      {
        key: 'checkbox',
        render: () => <CheckBox checked={isAll} handleCheck={handleAllCheck} />,
      },
      ...COLS.filter((col) => canViewUser || col !== '메모'),
      ...(isLottery ? ['선정상태'] : []),
    ],
    [isAll, handleAllCheck, isLottery, canViewUser],
  );

  const renderer: Record<
    (typeof COLS)[number],
    (data: IEnroll & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      checkbox: (data: IEnroll) => (
        <CheckBox
          checked={checkedData[data.id] || false}
          disabled={data.attendanceStatus === 'CANCEL'}
          handleCheck={(checked) => handleCheck(data.id, checked)}
        />
      ),
      참여유형: ({ applicationType }: IEnroll) => (
        <>
          {getApplicationTypeDetailLabel(
            applicationType,
            event?.applicationTypeQuotaTitle,
          )}
        </>
      ),
      이름: ({ user: { name, id } }: IEnroll) =>
        canViewUser ? (
          <Link to={`${USER_MENU_URL.회원목록}/${id}`}>{name}</Link>
        ) : (
          <>{name}</>
        ),
      성별: ({ user: { gender } }: IEnroll) => (
        <>{gender === 'MALE' ? '남' : '여'}</>
      ),
      연락처: ({ user: { phone } }: IEnroll) => <>{formatPhoneNumber(phone)}</>,
      신청일: ({ enrollDate }: IEnroll) => (
        <>{dayjs(enrollDate).format('YYYY-MM-DD HH:mm:ss.SSS')}</>
      ),
      최근참여일: ({ lastParticipationDate }: IEnroll) => (
        <>
          {lastParticipationDate
            ? dayjs(lastParticipationDate).format('YYYY-MM-DD HH:mm:ss')
            : '-'}
        </>
      ),
      출결상태: ({ attendanceStatus }: IEnroll) => (
        <div css={{ width: '100px' }}>
          {getAttendanceStatus(attendanceStatus)}
        </div>
      ),
      피드백작성여부: ({ id, user }: IEnroll) => (
        <button
          onClick={() => handleOpenFeedback({ enrollId: id, userId: user.id })}
        >
          상세보기
        </button>
      ),
      추가정보: ({ id, user }: IEnroll) => (
        <button
          onClick={() =>
            handleOpenAdditional({ enrollId: id, userId: user.id })
          }
        >
          상세보기
        </button>
      ),
      메모: ({ user }: IEnroll) => (
        <button onClick={() => hanldeOpenUserMemo(user.id)}>상세보기</button>
      ),
      // 어드민이 유형을 지정해 넣은 건은 추첨을 거치지 않아 선정 상태가 의미 없다.
      선정상태: ({
        selectionStatus,
        applicationType,
        attendanceStatus: attendance,
      }: IEnroll) =>
        applicationType ? (
          <>-</>
        ) : (
          <>
            {getSelectionStatus(selectionStatus)}
            {selectionStatus === 'SELECTED' && attendance === 'CANCEL'
              ? ' (취소)'
              : ''}
          </>
        ),
    }),
    [checkedData, handleCheck, event, canViewUser],
  );

  const deletableEnrolls = checkedEnrolls.filter((enroll) =>
    isAdminApplicationType(enroll.applicationType ?? null),
  );
  const excludedDeleteCount = checkedEnrolls.length - deletableEnrolls.length;

  const handleRemove = () => {
    const enrollIds = deletableEnrolls.map((enroll) => enroll.id);
    if (enrollIds.length === 0) return;

    removeEnrolls(
      { enrollIds },
      {
        onSuccess: () => {
          toast.success('참석자가 삭제되었습니다.');
        },
        onError: () => {
          toast.error(DEFAULT_ERROR_MESSAGE);
        },
      },
    );
  };

  const handleChangeStatus = (attendanceStatus: AttendanceStatusType) => {
    if (!event) return;
    if (event.eventStatus !== 'RECRUITMENT_CLOSED') {
      toast.error('모집 완료 후 출결 상태를 변경할 수 있습니다.');
      return;
    }

    const enrollIds = getCheckedEnrollIds();

    if (enrollIds.length === 0) return;
    mutate(
      {
        enrollIds,
        attendanceStatus,
      },
      {
        onSuccess: () => {
          toast.success('출결 상태가 변경되었습니다.');
        },
        onError: () => {
          toast.error(DEFAULT_ERROR_MESSAGE);
        },
      },
    );
  };

  const handlePostFeedbackNotification = () => {
    if (!event) return;
    if (event.eventStatus !== 'COMPLETED') {
      toast.error('진행 완료 후 피드백 알림을 보낼 수 있습니다.');
      return;
    }

    postFeedbackNotification(event.id, {
      onSuccess: () => {
        toast.success('피드백 알림이 전송되었습니다.');
      },
      onError: () => {
        toast.error(DEFAULT_ERROR_MESSAGE);
      },
    });
  };

  /**
   * 추첨 결과 확정.
   */
  const handleConfirmSelection = (enrollIds: number[]) => {
    if (!event) return;
    if (event.eventStatus !== 'RECRUITMENT_CLOSED') {
      toast.error('모집 완료 후 참여자를 확정할 수 있습니다.');
      return;
    }

    handleConfirm({
      confirmContent: (
        <>
          <p>
            총 신청자 {generalEnrollCount}명 중 {enrollIds.length}명을 최종
            참여자로 확정하시겠습니까?
          </p>
          <p css={{ marginTop: '8px', fontSize: '13px', color: '#8a8d94' }}>
            확정 후에는 되돌릴 수 없습니다.
          </p>
        </>
      ),
      yesTitle: '결과 확정',
      noTitle: '취소',
      confirmNext: () =>
        confirmSelection(
          { eventId: event.id, enrollIds },
          {
            onSuccess: () => toast.success('추첨 결과가 확정되었습니다.'),
            onError: (error) => toast.error(getErrorMessage(error)),
          },
        ),
    });
  };

  /** 추가 참여자 지정 — 미선정자 중에서 결원을 채운다. */
  const handleSelectAdditional = (enrollIds: number[]) => {
    if (!event) return;
    if (enrollIds.length === 0) {
      toast.error('추가로 지정할 신청자를 선택해주세요.');
      return;
    }

    handleConfirm({
      confirmContent: `${enrollIds.length}명을 추가 참여자로 지정하시겠습니까?`,
      yesTitle: '참여자 지정',
      noTitle: '취소',
      confirmNext: () =>
        selectAdditional(
          { eventId: event.id, enrollIds },
          {
            onSuccess: () => toast.success('추가 참여자가 지정되었습니다.'),
            onError: (error) => toast.error(getErrorMessage(error)),
          },
        ),
    });
  };

  return {
    data,
    pageState,
    cols,
    renderer,
    handleChangeStatus,
    handleRemove,
    deletableEnrolls,
    excludedDeleteCount,
    isRemoving,
    isChangingStatus,
    checkedEnrolls,
    handlePostFeedbackNotification,
    isLottery,
    hasSelected,
    generalEnrollCount,
    nameKeyword,
    handleSearchName,
    handleChangeAttendanceFilter,
    handleChangeSelectionFilter,
    selectedCount: getCheckedEnrollIds().length,
    handleConfirmSelection,
    handleSelectAdditional,
    isSelectionPending: isConfirming || isSelectingAdditional,
  };
}

export const getSelectionStatus = (status: SelectionStatusType | null) => {
  switch (status) {
    case 'APPLIED':
      return '신청 완료';
    case 'SELECTED':
      return '참여 확정';
    case 'NOT_SELECTED':
      return '미선정';
    default:
      return '-';
  }
};

const getErrorMessage = (error: unknown) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data
    ?.message ?? DEFAULT_ERROR_MESSAGE;

export const getAttendanceStatus = (status: AttendanceStatusType) => {
  switch (status) {
    case 'PRESENT':
      return '출석';
    case 'LATE':
      return '지각';
    case 'ABSENT':
      return '결석';
    case 'CANCEL':
      return '취소';
    case 'UNDETERMINED':
      return '미정';
    default:
      return '-';
  }
};

export default useVolunteerList;
